import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import './orderCorrection.css';
import {
  searchOrdersForCorrectionAPI,
  updateOrderCorrectionAPI,
  checkShiftStatusAPI,
} from './orderCorrectionAPI';
import { viewAllBranchesAPICall } from '../../../../API/Settings/Company Settings/companySettingsAPI';
import { paymentTypesForBilling } from '../../Sales/Billing/newBillingAPI';

const OrderCorrection = () => {
  const dispatch = useDispatch();
  const decimalPosition = localStorage.getItem('decimalPosition') || 2;

  // Redux states
  const { currencyDetails } = useSelector((state) => state.mainDashBoardSlice);
  const { paymentTypeList } = useSelector((state) => state.newBillingSlice);
  const branchList = useSelector((state) => state.allBranchesSlice.value);
  const userInfo = useSelector((state) => state.loginResponseSlice.value);

  // Component states
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [searchType, setSearchType] = useState('orderId');
  const [searchValue, setSearchValue] = useState('');
  const [ordersList, setOrdersList] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedOrder, setEditedOrder] = useState(null);
  const [shiftWarning, setShiftWarning] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load branches on mount
  useEffect(() => {
    console.log('🏢 Loading branches...');
    viewAllBranchesAPICall();
  }, []);

  // Load payment types when branch is selected
  useEffect(() => {
    if (selectedBranch?._id) {
      console.log('💳 Loading payment types for branch:', selectedBranch._id);
      paymentTypesForBilling({ branchId: selectedBranch._id });
    }
  }, [selectedBranch]);

  // Debug branch list and payment types
  useEffect(() => {
    console.log('📋 Branch list from Redux:', branchList);
    console.log('👤 User info:', userInfo);
    console.log('💳 Payment types:', paymentTypeList);
  }, [branchList, userInfo, paymentTypeList]);

  // Set default branch to user's branch
  useEffect(() => {
    if (branchList && branchList.length > 0 && !selectedBranch) {
      console.log('🎯 Setting default branch...');
      // Find user's branch or use first branch
      const userBranch = branchList.find(b => b._id === userInfo?.branch) || branchList[0];
      console.log('✅ Selected branch:', userBranch);
      setSelectedBranch(userBranch);
    }
  }, [branchList, userInfo]);

  const searchTypeOptions = [
    { label: 'Order ID', value: 'orderId' },
    { label: 'Bill Number', value: 'billNumber' },
    { label: 'Customer Mobile', value: 'customerMobile' },
    { label: 'Date Range', value: 'dateRange' },
  ];

  // View recent orders (for debugging/testing)
  const handleViewRecentOrders = async () => {
    if (!selectedBranch) {
      Swal.fire({
        icon: 'warning',
        title: 'Branch Required',
        text: 'Please select a branch first',
      });
      return;
    }

    console.log('📋 Fetching recent orders for branch:', selectedBranch._id);
    setLoading(true);
    try {
      // Search with empty date to get recent orders
      const today = new Date().toISOString().split('T')[0];
      const response = await searchOrdersForCorrectionAPI({
        branchId: selectedBranch._id,
        searchType: 'dateRange',
        searchValue: today,
      });
      
      console.log('✅ Recent orders:', response);
      
      if (response && response.length > 0) {
        setOrdersList(response);
        Swal.fire({
          icon: 'success',
          title: 'Recent Orders',
          text: `Found ${response.length} orders from today`,
        });
      } else {
        setOrdersList([]);
        Swal.fire({
          icon: 'info',
          title: 'No Orders Today',
          text: 'No orders found for today. Try searching by bill number or customer mobile.',
        });
      }
    } catch (error) {
      console.error('❌ Error fetching recent orders:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to Load Orders',
        text: error.message || 'Failed to fetch recent orders',
      });
    } finally {
      setLoading(false);
    }
  };

  // Search orders
  const handleSearch = async () => {
    if (!selectedBranch) {
      Swal.fire({
        icon: 'warning',
        title: 'Branch Required',
        text: 'Please select a branch first',
      });
      return;
    }

    if (!searchValue.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Search Value Required',
        text: 'Please enter a search value',
      });
      return;
    }

    console.log('🔍 Searching orders:', { branchId: selectedBranch._id, searchType, searchValue });
    setLoading(true);
    try {
      const response = await searchOrdersForCorrectionAPI({
        branchId: selectedBranch._id,
        searchType,
        searchValue: searchValue.trim(),
      });
      
      console.log('✅ Search response:', response);
      
      if (response && response.length > 0) {
        setOrdersList(response);
        console.log(`📋 Found ${response.length} orders`);
      } else {
        setOrdersList([]);
        Swal.fire({
          icon: 'info',
          title: 'No Orders Found',
          text: 'No orders match your search criteria',
        });
      }
    } catch (error) {
      console.error('❌ Search error:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to search orders';
      
      Swal.fire({
        icon: 'error',
        title: 'Search Failed',
        html: `<p>${errorMessage}</p>
               ${error.response?.data?.error ? `<pre style="text-align: left; font-size: 11px; max-height: 200px; overflow: auto;">${error.response.data.error}</pre>` : ''}`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Open edit dialog
  const handleEditClick = async (order) => {
    setSelectedOrder(order);
    
    // Map order items with correct field names
    const mappedOrderInfo = order.orderInfo.map((item) => ({
      ...item,
      // Map backend field names to frontend expected names
      price: item.unitPrice || item.price || 0,
      total: item.lineTotal || item.total || 0,
      description: item.desccription || item.description || '',
    }));

    // Recalculate totals based on items
    const subTotal = mappedOrderInfo.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxAmt = order.taxAmt || 0;
    const discountAmt = order.discountAmt || 0;
    const deliveryCharge = order.deliveryCharge || 0;
    const packagingCharge = order.packagingCharge || order.pkgCharge || 0;
    const roundOff = order.roundOff || 0;
    const totalAmt = subTotal + taxAmt - discountAmt + deliveryCharge + packagingCharge + roundOff;

    // Store ALL payment info for audit trail
    const originalPayments = order.paymentInfo ? [...order.paymentInfo] : [];
    
    // For orders with multiple payments, show only the LATEST one (most recent correctedAt or highest amount)
    // Old payments go to audit trail
    let activePayments = [];
    if (order.paymentInfo && order.paymentInfo.length > 0) {
      // Sort by correctedAt (newest first), or by createdAt if no corrections
      const sortedPayments = [...order.paymentInfo].sort((a, b) => {
        const aDate = a.correctedAt || a.createdAt || 0;
        const bDate = b.correctedAt || b.createdAt || 0;
        return new Date(bDate) - new Date(aDate);
      });
      
      // Take only the first (most recent) payment
      activePayments = [sortedPayments[0]];
    }
    
    console.log('📋 Payment filtering:', {
      total: originalPayments.length,
      active: activePayments.length,
      originalPayments,
      activePayments,
      showingLatest: activePayments[0]
    });

    setEditedOrder({
      ...order,
      orderInfo: mappedOrderInfo,
      paymentInfo: activePayments, // Only show active payments
      originalPayments: originalPayments, // Store all for audit trail
      subTotal,
      totalAmt,
      packagingCharge, // Ensure packagingCharge is set correctly
    });

    // Check if shift is closed
    try {
      const shiftStatus = await checkShiftStatusAPI({ shiftId: order.shiftId });
      if (shiftStatus.isClosed) {
        setShiftWarning({
          message: `⚠️ WARNING: This order belongs to a CLOSED shift (${shiftStatus.shiftName}). Any corrections will require MANUAL JOURNAL UPDATES to maintain accounting integrity.`,
          shiftData: shiftStatus,
        });
      } else {
        setShiftWarning(null);
      }
    } catch (error) {
      console.error('Shift check error:', error);
    }

    setEditDialogOpen(true);
  };

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setEditedOrder((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle item changes
  const handleItemChange = (index, field, value) => {
    setEditedOrder((prev) => {
      const newOrderInfo = [...prev.orderInfo];
      newOrderInfo[index] = {
        ...newOrderInfo[index],
        [field]: parseFloat(value) || 0,
      };

      // Recalculate item total
      if (field === 'qty' || field === 'price') {
        newOrderInfo[index].total =
          newOrderInfo[index].qty * newOrderInfo[index].price;
      }

      // Recalculate order totals
      const subTotal = newOrderInfo.reduce((sum, item) => sum + item.total, 0);
      const taxAmt = prev.taxAmt || 0;
      const discountAmt = prev.discountAmt || 0;
      const deliveryCharge = prev.deliveryCharge || 0;
      const packagingCharge = prev.packagingCharge || 0;
      const roundOff = prev.roundOff || 0;

      const totalAmt =
        subTotal + taxAmt - discountAmt + deliveryCharge + packagingCharge + roundOff;

      return {
        ...prev,
        orderInfo: newOrderInfo,
        subTotal,
        totalAmt,
      };
    });
  };

  // Handle payment changes
  const handlePaymentChange = (index, field, value) => {
    setEditedOrder((prev) => {
      const newPaymentInfo = [...prev.paymentInfo];
      newPaymentInfo[index] = {
        ...newPaymentInfo[index],
        [field]: field === 'amount' ? parseFloat(value) || 0 : value,
      };
      return {
        ...prev,
        paymentInfo: newPaymentInfo,
      };
    });
  };

  // Handle discount change
  const handleDiscountChange = (value) => {
    const discountAmt = parseFloat(value) || 0;
    setEditedOrder((prev) => {
      const subTotal = prev.subTotal || 0;
      const taxAmt = prev.taxAmt || 0;
      const deliveryCharge = prev.deliveryCharge || 0;
      const packagingCharge = prev.packagingCharge || 0;
      const roundOff = prev.roundOff || 0;

      const totalAmt =
        subTotal + taxAmt - discountAmt + deliveryCharge + packagingCharge + roundOff;

      return {
        ...prev,
        discountAmt,
        totalAmt,
      };
    });
  };

  // Save corrections
  const handleSaveCorrection = async () => {
    // Validation
    if (!editedOrder) return;

    // Note: We don't validate payment match because:
    // 1. DRAFT orders may not have payments yet
    // 2. We're correcting orders, so mismatches are expected
    // 3. Backend will handle payment updates separately

    // Show confirmation with shift warning
    const confirmResult = await Swal.fire({
      title: 'Confirm Order Correction',
      html: `
        <div style="text-align: left;">
          <p><strong>Order:</strong> ${editedOrder.ordName}</p>
          <p><strong>Original Total:</strong> ${currencyDetails?.currencySymbol || ''}${selectedOrder.totalAmt.toFixed(decimalPosition)}</p>
          <p><strong>New Total:</strong> ${currencyDetails?.currencySymbol || ''}${editedOrder.totalAmt.toFixed(decimalPosition)}</p>
          ${
            shiftWarning
              ? `<div style="background: #fff3cd; padding: 10px; margin-top: 10px; border-radius: 5px; border-left: 4px solid #ffc107;">
                  <strong style="color: #856404;">⚠️ SHIFT CLOSED WARNING</strong>
                  <p style="color: #856404; margin: 5px 0;">${shiftWarning.message}</p>
                  <p style="color: #856404; margin: 5px 0;"><strong>You MUST manually update the accounting journals after this correction!</strong></p>
                </div>`
              : ''
          }
        </div>
      `,
      icon: shiftWarning ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: 'Save Correction',
      cancelButtonText: 'Cancel',
      confirmButtonColor: shiftWarning ? '#ff9800' : '#3085d6',
    });

    if (!confirmResult.isConfirmed) return;

    setLoading(true);
    try {
      const response = await updateOrderCorrectionAPI({
        orderId: editedOrder._id,
        updates: {
          orderInfo: editedOrder.orderInfo,
          paymentInfo: editedOrder.paymentInfo,
          subTotal: editedOrder.subTotal,
          totalAmt: editedOrder.totalAmt,
          discountAmt: editedOrder.discountAmt,
          taxAmt: editedOrder.taxAmt,
          deliveryCharge: editedOrder.deliveryCharge,
          packagingCharge: editedOrder.packagingCharge,
          roundOff: editedOrder.roundOff,
        },
        shiftClosed: shiftWarning ? true : false,
      });

      if (response.success) {
        const updatedOrder = response.data;
        const hasRemainingBalance = updatedOrder.amountDue > 0;

        // Close the edit dialog first
        setEditDialogOpen(false);
        setSelectedOrder(null);
        setEditedOrder(null);
        setShiftWarning(null);

        if (hasRemainingBalance) {
          // Show success message with payment prompt
          const result = await Swal.fire({
            icon: 'success',
            title: 'Order Corrected Successfully',
            html: `
              <p>Order has been updated.</p>
              <p style="color: #ff9800; font-weight: bold;">
                Remaining Balance: ${currencyDetails?.currencySymbol || ''}${updatedOrder.amountDue.toFixed(decimalPosition)}
              </p>
              <p>Would you like to process the payment now?</p>
            `,
            showCancelButton: true,
            confirmButtonText: 'Process Payment',
            cancelButtonText: 'Later',
            confirmButtonColor: '#4caf50',
          });

          if (result.isConfirmed) {
            // Show instructions to complete payment
            Swal.fire({
              icon: 'info',
              title: 'Complete Payment',
              html: `
                <p><strong>Remaining Balance:</strong> ${currencyDetails?.currencySymbol || ''}${updatedOrder.amountDue.toFixed(decimalPosition)}</p>
                <p>Please go to the order in POS and complete the payment.</p>
                <p><strong>Order Number:</strong> ${updatedOrder.name || updatedOrder.ordName}</p>
              `,
              confirmButtonText: 'OK',
            }).then(() => {
              handleSearch();
            });
          } else {
            // Just refresh the search
            handleSearch();
          }
        } else {
          // No remaining balance, just show success
          Swal.fire({
            icon: 'success',
            title: 'Order Corrected Successfully',
            html: shiftWarning
              ? `<p>Order updated successfully.</p>
                 <p style="color: #d32f2f; font-weight: bold;">⚠️ IMPORTANT: Please update the accounting journals manually!</p>
                 <p>Shift: ${shiftWarning.shiftData.shiftName}</p>`
              : 'Order has been corrected and journals updated automatically.',
          });

          // Refresh search
          handleSearch();
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Correction Failed',
        text: error.message || 'Failed to save corrections',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="order-correction-container">
      <Typography variant="h6" className="breadcrumb">
        Order Correction Utility
      </Typography>

      {/* Alert if no branches */}
      {(!branchList || branchList.length === 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No branches available. Please ensure you have access to at least one branch.
        </Alert>
      )}

      {/* Search Section */}
      <Paper className="search-section" elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={branchList || []}
              getOptionLabel={(option) => option?.nameOfStore || option?.name || ''}
              value={selectedBranch || null}
              onChange={(e, newValue) => {
                console.log('🔄 Branch changed to:', newValue);
                setSelectedBranch(newValue);
                setOrdersList([]); // Clear results when branch changes
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Branch *" 
                  size="small"
                  placeholder={!branchList || branchList.length === 0 ? "Loading branches..." : "Select branch"}
                />
              )}
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              loading={!branchList}
              disabled={!branchList || branchList.length === 0}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Autocomplete
              options={searchTypeOptions}
              getOptionLabel={(option) => option.label}
              value={searchTypeOptions.find((opt) => opt.value === searchType)}
              onChange={(e, newValue) => setSearchType(newValue?.value || 'orderId')}
              renderInput={(params) => (
                <TextField {...params} label="Search By" size="small" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search Value"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={
                searchType === 'orderId'
                  ? 'Enter Order ID or Bill Number'
                  : searchType === 'billNumber'
                  ? 'Enter Bill Number (e.g., 37921 or BIL/37921)'
                  : searchType === 'customerMobile'
                  ? 'Enter Customer Mobile'
                  : 'Enter Date (YYYY-MM-DD)'
              }
              helperText={
                searchType === 'billNumber' 
                  ? 'You can enter just the number (37921) or full format (BIL/37921)'
                  : ''
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={handleViewRecentOrders}
              disabled={loading || !selectedBranch}
              title="View today's orders"
            >
              Today
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Orders List */}
      {ordersList.length > 0 && (
        <TableContainer component={Paper} className="orders-table" elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Bill No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Shift</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ordersList.map((order) => (
                <TableRow key={order._id} hover>
                  <TableCell>{order.ordName || '-'}</TableCell>
                  <TableCell>
                    {new Date(order.orderDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{selectedBranch?.nameOfStore || selectedBranch?.name || '-'}</TableCell>
                  <TableCell>
                    {order.customerName || 'Cash Customer'}
                    {order.customerMobile && (
                      <div style={{ fontSize: '0.85em', color: '#666' }}>
                        {order.customerMobile}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{order.ordType || '-'}</TableCell>
                  <TableCell align="right">
                    {currencyDetails?.currencySymbol || ''}
                    {order.totalAmt?.toFixed(decimalPosition) || '0.00'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.ordStatus || 'PENDING'}
                      size="small"
                      color={
                        order.ordStatus === 'COMPLETED'
                          ? 'success'
                          : order.ordStatus === 'CANCELLED'
                          ? 'error'
                          : 'warning'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {order.shiftName || '-'}
                    {order.shiftClosed && (
                      <Chip
                        label="CLOSED"
                        size="small"
                        color="error"
                        icon={<WarningAmberIcon />}
                        style={{ marginLeft: 5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditClick(order)}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Correct Order: {editedOrder?.ordName}
          {shiftWarning && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 1 }}>
              {shiftWarning.message}
            </Alert>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {editedOrder && (
            <Box>
              {/* Order Items */}
              <Typography variant="subtitle1" gutterBottom>
                <strong>Order Items</strong>
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedOrder.orderInfo.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description || item.productName || item.name || 'Unknown Product'}</TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={item.qty}
                            onChange={(e) =>
                              handleItemChange(index, 'qty', e.target.value)
                            }
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={item.price}
                            onChange={(e) =>
                              handleItemChange(index, 'price', e.target.value)
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {currencyDetails?.currencySymbol || ''}
                          {item.total?.toFixed(decimalPosition) || '0.00'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Order Totals */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Discount Amount"
                    type="number"
                    size="small"
                    value={editedOrder.discountAmt || 0}
                    onChange={(e) => handleDiscountChange(e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Total Amount"
                    size="small"
                    value={editedOrder.totalAmt?.toFixed(decimalPosition) || '0.00'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>

              {/* Payment Information */}
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                <strong>Payment Information</strong>
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Note:</strong> You can only change the payment TYPE (Cash/Card/UPI). Payment amounts are read-only.
              </Alert>
              {editedOrder.paymentInfo && editedOrder.paymentInfo.length > 0 ? (
                <>
                  <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Payment Type</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Approval Code</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {editedOrder.paymentInfo?.map((payment, index) => (
                      <TableRow key={payment._id || index}>
                        <TableCell>
                          <Autocomplete
                            options={paymentTypeList || []}
                            getOptionLabel={(option) => option.type || ''}
                            value={
                              paymentTypeList?.find(
                                (pt) => pt._id === payment.paymentJournelId
                              ) || null
                            }
                            onChange={(e, newValue) => {
                              handlePaymentChange(
                                index,
                                'paymentJournelId',
                                newValue?._id
                              );
                              // Also update the type name
                              if (newValue) {
                                handlePaymentChange(
                                  index,
                                  'typeName',
                                  newValue.type
                                );
                              }
                            }}
                            renderInput={(params) => (
                              <TextField {...params} size="small" />
                            )}
                            sx={{ minWidth: 150 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={payment.amount}
                            InputProps={{ readOnly: true }}
                            sx={{ width: 120, bgcolor: '#f5f5f5' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={payment.approvalCode || ''}
                            onChange={(e) =>
                              handlePaymentChange(
                                index,
                                'approvalCode',
                                e.target.value
                              )
                            }
                            placeholder="Approval code"
                            sx={{ width: 150 }}
                          />
                        </TableCell>
                      </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Payment Summary */}
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Payment Summary</strong>
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Order Total:
                        </Typography>
                        <Typography variant="h6">
                          {currencyDetails?.currencySymbol || ''}
                          {editedOrder.totalAmt?.toFixed(decimalPosition) || '0.00'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Paid:
                        </Typography>
                        <Typography variant="h6">
                          {currencyDetails?.currencySymbol || ''}
                          {editedOrder.paymentInfo.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(decimalPosition)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Amount Due:
                        </Typography>
                        <Typography 
                          variant="h6" 
                          color={editedOrder.amountDue > 0 ? 'error' : 'success'}
                          sx={{ fontWeight: 'bold' }}
                        >
                          {currencyDetails?.currencySymbol || ''}
                          {editedOrder.amountDue?.toFixed(decimalPosition) || '0.00'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Audit Trail - Show if order was previously corrected */}
                  {(editedOrder.isCorrected || editedOrder.correctedAt || (editedOrder.originalPayments && editedOrder.originalPayments.length > editedOrder.paymentInfo.length)) && (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
                        <strong>📋 Correction History</strong>
                      </Typography>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {editedOrder.correctedAt && (
                          <>
                            <Typography variant="body2">
                              <strong>Last Corrected:</strong> {new Date(editedOrder.correctedAt).toLocaleString()}
                            </Typography>
                            {editedOrder.correctedBy && (
                              <Typography variant="body2">
                                <strong>Corrected By:</strong> {editedOrder.correctedBy}
                              </Typography>
                            )}
                          </>
                        )}
                        
                        {/* Show original payment data if available */}
                        {editedOrder.originalPayments && editedOrder.originalPayments.length > 0 && (
                          <Box sx={{ mt: 2, p: 1, bgcolor: '#fff3cd', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                              Original Payment Records:
                            </Typography>
                            {editedOrder.originalPayments.map((payment, idx) => (
                              <Typography key={idx} variant="caption" sx={{ display: 'block' }}>
                                • {paymentTypeList?.find(pt => pt._id === payment.paymentJournelId)?.type || 'Unknown'}: {currencyDetails?.currencySymbol || ''}{payment.amount?.toFixed(decimalPosition) || '0.00'}
                                {payment.approvalCode && ` (${payment.approvalCode})`}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Any changes you make will update the order and related journals.
                        </Typography>
                      </Alert>
                    </>
                  )}
                </>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No payments recorded for this order yet.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditDialogOpen(false)}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveCorrection}
            variant="contained"
            color={shiftWarning ? 'warning' : 'primary'}
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {shiftWarning ? 'Save (Manual Journal Update Required)' : 'Save Correction'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrderCorrection;
